import { Inject } from '@odinjs/odin';
import Controller, { OnBeforeFetch, OnBeforeSubmit, OnBlur, OnChange, OnClear, OnClick, OnMount, OnReadSuccess, OnUnmount, OnValidate } from '@philips-internal/oswald-ui-core/lib/client';
import { HttpService, MessageService,  } from '@philips-internal/oswald-ui-core/lib/services';
import BaseController from '../BaseController';
import { applicationContext } from '@philips-internal/oswald-ui-foundation';
import { Endpoint } from '../../constants/endpoints';
import { ApplicationRoute } from '../../constants/routes';
import { SearchPatientEcAliases as Aliases, FragmentNames } from '../../codegen';
import { Placement, ToastLevel, ToastType } from '@philips-internal/oswald-ui-constants';
import { ChangeEvent, ClickEvent, FetchEvent, MountEvent, ReadEvent, ValidateEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import './search-patient-ec-controller.css';
import { AutocompleteAPI, BarcodeAPI, BarcodeService as BarcodeApiService, BarcodeData, BarcodeReaderType, BaseToastProps, FetchAPI, FieldAPI, FilterAPI, MapperAPI, PanelAPI, ToastService } from '@philips-internal/oswald-ui-components';
import { isFunction } from '@philips-internal/oswald-ui-utils';
import { Patient } from '../../interfaces/exam-collect/patiente-data-props';
import { Value } from '@philips-internal/oswald-ui-core';
import { ImageUtils } from '../../utils/image-utils';
import BarcodeService from '../../services/barcode-service';

interface EncounterStatusResponse {
  ieAtendValido: string;
}

interface PatientMapper {
  cdPessoaFisica: string;
  genero: string;
  idade: string;
  nome: string;
  nrAtendimento: number;
  photo: string;
}

const barcodeServiceConfirmationId = 'barcode-service-confirmation-id'
const toastErrorId = 'search-patient-controller.no-access-to-encounter';

@Controller({ component: Aliases.SEARCH_PATIENT_LAYOUT, fragment: FragmentNames.SEARCH_PATIENT_EC })
class SearchPatientECController extends BaseController {

  @Inject(Aliases.FILTER_PATIENTS)
  private filterPatients!: FilterAPI;

  @Inject(Aliases.FILTER_FIELD_ENCOUNTER)
  private encounterNumberField!: FieldAPI;

  @Inject(Aliases.FIELD_DEPARTMENTS)
  private departmentsField!: FieldAPI;

  @Inject(Aliases.AUTOCOMPLETE_DEPARTMENTS)
  private autoCompletDepartments!: AutocompleteAPI;

  @Inject(Aliases.BARCODE_COMPONENT)
  private barcodeComponentApi!: BarcodeAPI;

  @Inject(Aliases.PANEL_MODAL)
  private panelLoaderApi!: PanelAPI;

  @OnClear({ target: Aliases.FILTER_PATIENTS })
  onFilterClear(): void {
    this.filterPatients.clear();
    this.autoCompletDepartments.setValue({} as Record<string, Value>)
  }

  goToBraceletConfirmation(event: ClickEvent): void {
    const { cdPessoaFisica, nrAtendimento } = event.value;

    BarcodeApiService.open({
      data: [],
      defaultReadType: BarcodeReaderType.CAMERA,
      hasDescription: false,
      id: barcodeServiceConfirmationId,
      infraredInputSubmitExpression: 'tws.barcode.manual-submit',
      manualInputBarcodeExpression: 'tws.barcode.wristband',
      onReadSuccess: (barcodeEvent: ReadEvent<BarcodeData>) => {
        this.onReadSucessBraceletConfirmationBarcode(barcodeEvent, String(cdPessoaFisica), String(nrAtendimento));
      },
      requireDescription: true,
      requireReason: true,
      supportCamera: true,
      supportInfrared: true
    });
  }

  onReadSucessBraceletConfirmationBarcode(barcodeEvent: ReadEvent<BarcodeData>, patientId: string, encounterId: string): void {
    this.validateBarcode(barcodeEvent);
    const braceletNumber = barcodeEvent.item.value;

    if (encounterId !== braceletNumber) {
      this.errorMessageBraceletConfirmationBarcode();
      return;
    }

    applicationContext.redirect(ApplicationRoute.EXAM_COLLECT_PRESCRIPTIONS_DETAIL, {
      encounterId,
      patientId,
    })
    BarcodeApiService.close(barcodeServiceConfirmationId)
  }

  errorMessageBraceletConfirmationBarcode(): void{
    const errorMenssage = MessageService.translate('text.the-beep-bracelet-is-different-from-the-service-previously-selected');
    const titleMessage = MessageService.translate('text.message-alert-attention');
    ToastService.close('custom-toast');
    ToastService.open({
      dismissible: true,
      id: 'custom-toast',
      level: ToastLevel.ERROR,
      message: errorMenssage,
      placement: Placement.BOTTOM,
      showIcon: true,
      timeout: 5000,
      title: titleMessage,
      type: ToastType.BUBBLE,
    });
  }

  errorMessage(): void{
    const errorMenssage = MessageService.translate('tws.search-patient-ec.invalid-code-error');
    const titleMessage = MessageService.translate('text.message-alert-attention');
    ToastService.close('custom-toast');
    ToastService.open({
      dismissible: true,
      id: 'custom-toast',
      level: ToastLevel.ERROR,
      message: errorMenssage,
      placement: Placement.BOTTOM,
      showIcon: true,
      timeout: 5000,
      title: titleMessage,
      type: ToastType.BUBBLE,
    });
  }

  validateBarcode(event: ReadEvent<BarcodeData>): void{
    const inputValue = event.item.value;
    const braceletNumber = Number(inputValue);

    if (!braceletNumber) {
      this.errorMessage();
      return;
    }

    const {preventDefault} = event;
    if (isFunction(preventDefault)) {
      preventDefault();
    }
  }

  @OnMount(Aliases.BARCODE_COMPONENT)
  handleMountBarcode(event: MountEvent): void {
    const barcodeApi = event.target as BarcodeAPI
    barcodeApi.setDefaultReadType(BarcodeService.getDefaultMethodOfReadingBarcode())
  }

  @OnReadSuccess({ target: Aliases.BARCODE_COMPONENT })
  onReadSucesstBarCode(event: ReadEvent<BarcodeData>): void {
    const barcode = event.target as BarcodeAPI;
    this.validateBarcode(event);

    const queryParams = {
      limit: 12,
      nrAtendimento: event.item.value.trim(),
      page: 0,
      readingMode: event.item.type,
      ignorarSaidaSetor: true
    }
    barcode.setEnabled(false);
    this.panelLoaderApi.setVisible(true);

    HttpService.get(Endpoint.GET_PATIENT_LIST, queryParams).then(resp=>{
      const body = resp.body as Patient[];
      barcode.setEnabled(true);
      if (body.length === 0) {
        this.errorMessage();
        this.panelLoaderApi.setVisible(false);
        barcode.setOpen(true);
      }else{
        barcode.close();
        this.getPatientAndRedirect(queryParams.nrAtendimento);
      }
    }).catch(() => {
      barcode.setEnabled(true);
      barcode.setOpen(true);
      this.panelLoaderApi.setVisible(false);
    })
  }

  @OnChange({target: Aliases.FILTER_FIELD_ENCOUNTER })
  onEncounterNumberChange(evt: ChangeEvent<FieldAPI>): void {
    const isChangeValid = !!evt.value;
    this.context.set({ encounterNumber: evt.value });
    if(isChangeValid){
      this.departmentsField.setValue(null);
    }
  }

  @OnValidate({target: Aliases.FILTER_FIELD_ENCOUNTER })
  onEncounterNumberValidate(evt: ValidateEvent): void {
    this.context.set({
      valid: evt.valid
    })
  }

  @OnMount(Aliases.PATIENT_FETCH_MAPPER)
  patientFetchMapper(onMountEvent: MountEvent): void {
    const mapperEvent = onMountEvent.target as MapperAPI;

    mapperEvent.addCustomMapping('subtitle', data  => {
      const dataItem = data as unknown as PatientMapper;
      return `${dataItem.idade} / ${dataItem.genero} / ${dataItem.nrAtendimento}`;
    });

    mapperEvent.addCustomMapping('photo', data  => {
      const dataItem = data as unknown as PatientMapper;
      return ImageUtils.getImageAvatar(dataItem.photo as string)
    });
  }

  @OnBlur({ target: 'input-encounter' })
  onFilterFieldBlur(): void {
    const validateResult = this.context.get('valid') as boolean;
    const nrAtendimento = this.context.get('encounterNumber') as string;

    ToastService.close(toastErrorId);

    if(validateResult && nrAtendimento?.length > 0){
      this.checkEncounterNumberSTatus(nrAtendimento);
    }
  }

  @OnBeforeSubmit({target: Aliases.FILTER_PATIENTS })
  onBeforeSubmitFilter = this.onFilterFieldBlur;

  @OnClick({ target: Aliases.PATIENT_FETCH_DECK })
  onClickBraceletConfirmation(event: ClickEvent): void {
    this.goToBraceletConfirmation(event)
  }

  checkEncounterNumberSTatus(nrAtendimento: string): void {
    HttpService.get(Endpoint.ENCOUNTER_STATUS, { nrAtendimento }).then((resp)=>{
      const respStatus = resp.body as EncounterStatusResponse;
      const validStatus = respStatus.ieAtendValido;

      switch(validStatus){
        case 'A':
          this.addEncounterNumberFieldMessage('tws.search-patient-ec.discharge-data-error');
          break;
        case 'V':
          this.getPatientAndRedirect(nrAtendimento);
          break;
        case 'N':
          this.addEncounterNumberFieldMessage('tws.search-patient-ec.invalid-code-error');
          break;
      }
    }).catch((err)=>{
      this.addEncounterNumberFieldMessage(err?.body);
    })
  }

  @OnUnmount({ target: Aliases.SEARCH_PATIENT_LAYOUT })
  onUnmountLayout(): void {
    this.context.set({
      encounterNumber: '',
      valid: false,
    });
  }

  showErrorToast(message: string): void {
    const toast: BaseToastProps = {
      dismissible: true,
      id: toastErrorId,
      level: ToastLevel.ERROR,
      message: MessageService.translate(message),
      type: ToastType.BUBBLE,
    };
    ToastService.open(toast)
  }

  goToInformationBar(cdPaciente: string, nrAtendimento: string): void {
    const route = ApplicationRoute.EXAM_COLLECT_PRESCRIPTIONS_DETAIL

    HttpService.get(Endpoint.USER_HAS_EMR_ACCESS_TO_ENCOUNTER_ESTABLISHMENT, { cdPaciente, encounterId: nrAtendimento })
      .then(() => {
        this.panelLoaderApi.setVisible(false);
        applicationContext.redirect(route, {
          encounterId: nrAtendimento,
          isExamCollect: 1,
          patientId: cdPaciente,
        });
      })
      .catch((err) => {
        this.barcodeComponentApi.setOpen(true);
        this.panelLoaderApi.setVisible(false);
        this.addEncounterNumberFieldMessage(err?.body)
      });
  }

  getPatientAndRedirect(nrAtendimento: string): void {
    const queryParams = `?nrAtendimento=${nrAtendimento}&page=0&limit=12&ignorarSaidaSetor=true`;
    const route = `${Endpoint.GET_PATIENT_LIST}${queryParams}`;

    HttpService.get(route).then(resp=>{
      const body = resp.body as Patient[];
      const cdPaciente = body[0].cdPessoaFisica;

      this.goToInformationBar(cdPaciente, nrAtendimento);
    }).catch((err)=>{
      this.panelLoaderApi.setVisible(false);
      this.addEncounterNumberFieldMessage(err?.body);
    })
  }

  addEncounterNumberFieldMessage(message: string, name?: string): void {
    try{
      const validatorObj = {
        message: MessageService.translate(message),
        name: name || '',
        valid: false
      }
      const validatorName = 'encounter-code-status-validator';
      this.encounterNumberField.addLocalValidator(validatorName, async () => ([validatorObj]));
      this.encounterNumberField.validate();
      this.encounterNumberField.removeLocalValidator(validatorName);

    } finally {
      this.showErrorToast(message);
    }
  }

  @OnBeforeFetch({ target: Aliases.FETCH_DEPARTMENTS })
  onBeforeFetchDepartment(evt: FetchEvent): void {
    const event = evt.target as FetchAPI;
    if (evt) {
      const params = {
        dsSetorAtendimento: this.departmentsField.getValue(),
        emar: false
      }

      event.setParameters(params);
    }
  }
}

export default SearchPatientECController;
